import type { Role, UserRow } from '../types/UserTable.types';

/**
 * Derives a role→hierarchy lookup and the current user's highest (lowest-numbered) role
 * from the full roles list. (Moved here for deprecation of buildRoleHierarchy.)
 */
export function buildRoleHierarchy(
  allRoles: Role[],
  username: string,
): { hierarchy: Record<string, number>; yourHighestRoleHierarchyId: number } {
  const eligible = allRoles.filter((item) => parseInt(String(item.role_Id)) >= 0);

  const hierarchy: Record<string, number> = {};
  eligible.forEach((role) => {
    hierarchy[String(role.role_Id)] = role.hierarchy_level ?? 999;
  });

  const myRoleIds = new Set(
    allRoles
      .filter((item) => item.users?.includes(username))
      .map((item) => String(item.role_Id)),
  );

  let yourHighestRoleHierarchyId = 999;
  eligible.forEach((role) => {
    if (myRoleIds.has(String(role.role_Id))) {
      const h = role.hierarchy_level ?? 999;
      if (h < yourHighestRoleHierarchyId) yourHighestRoleHierarchyId = h;
    }
  });

  return { hierarchy, yourHighestRoleHierarchyId };
}

/**
 * Given a parsed roles array (or string/other), return the lowest-numbered
 * hierarchy level found for those roles. Falls back to 999 when none are found.
 */
export function getHighestRoleHierarchy(
  roles: number[] | string | undefined | null,
  hierarchy: Record<string, number> = {},
): number {
  const parsedRoles = typeof roles === 'string' ? JSON.parse(roles) : roles || [];
  let highestHierarchyRole = 999;
  (parsedRoles || []).forEach((r: number | string) => {
    const roleNum = Number(r);
    if (Number.isNaN(roleNum) || roleNum < 0) return;
    const h = hierarchy[String(roleNum)];
    if (typeof h !== 'undefined' && h <= highestHierarchyRole) highestHierarchyRole = h;
  });
  return highestHierarchyRole;
}

/**
 * Returns true when the provided roles map to a hierarchy level that is shared
 * with or higher (i.e. <=) than `yourHighestRoleHierarchyId`, taking into
 * account any permission overrides (e.g., accessLevel 8 hides sharing).
 */
export function userHasSharedHierarchyOrHigher(
  roles: number[] | string | undefined | null,
  hierarchy: Record<string, number> = {},
  yourHighestRoleHierarchyId: number = 999,
  permsToUse?: any,
): boolean {
  if (permsToUse?.accessLevel === 8) return false;
  const highest = getHighestRoleHierarchy(roles, hierarchy);
  return highest <= (yourHighestRoleHierarchyId ?? 999);
}

export interface MapWorkplaceMembersOptions {
  username?: string | null;
  hierarchy?: Record<string, number>;
  yourHighestRoleHierarchyId?: number;
  permsToUse?: any;
}

export function mapWorkplaceMembersToUserRows(
  members: any[] = [],
  { username, hierarchy = {}, yourHighestRoleHierarchyId = 999, permsToUse = {} }: MapWorkplaceMembersOptions = {},
): UserRow[] {
  if (!Array.isArray(members) || members.length === 0) return [];

  const sorted = [...members].sort((a, b) => {
    const aTime = new Date(a.date_added).valueOf() || 0;
    const bTime = new Date(b.date_added).valueOf() || 0;
    return bTime - aTime;
  });

  if (username) {
    const idx = sorted.findIndex((u) => String(u.email) === String(username));
    if (idx > 0) {
      const [me] = sorted.splice(idx, 1);
      sorted.unshift(me);
    }
  }

  return sorted.map((user) => {
    let sharedHierarchyOrHigher = false;
    try {
      sharedHierarchyOrHigher = userHasSharedHierarchyOrHigher(
        user.roles,
        hierarchy,
        yourHighestRoleHierarchyId,
        permsToUse,
      );
    } catch (e) {
      sharedHierarchyOrHigher = false;
    }

    return {
      userId: user.userId,
      id: user.userId,
      activated: user.activated,
      name: user.name,
      role_name: user.roles,
      email: user.email,
      create_by_email: user.create_by_email,
      date_added: new Date(user.date_added).toLocaleString([], {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
      }),
      perm_level: user.perm_level,
      raw: user,
      sharedHierarchyOrHigher,
    } as UserRow;
  });
}

export default mapWorkplaceMembersToUserRows;

/**
 * Derives showViewOnly / canEdit / canDelete flags for a single user row.
 *
 * showViewOnly — ellipsis is blocked; eye icon is the fallback when onView is provided.
 * canEdit      — current user may edit this row (still requires onEdit handler).
 * canDelete    — current user may delete this row (still requires onDelete handler).
 *
 * Mirrors the MembersTab.jsx visibility rules:
 *   Ellipsis shown when:
 *     - not the currently logged-in user
 *     - access level permits editing this perm_level (owner→≤12, admin→≤10, normal→≤10, 8=bypass)
 *     - hierarchy check passes or is bypassed for admin/owner
 *     - current user has Create-EditUsers permission
 */
export function getUserActionFlags(
  user: UserRow,
  perms: any,
  currentUsername: string | undefined,
): { showViewOnly: boolean; canEdit: boolean; canDelete: boolean } {
  const accessLevel = perms?.accessLevel;
  const { email, perm_level, sharedHierarchyOrHigher } = user;

  // Permission check helper (tests frequently mock `testPermission` without
  // providing `accessLevel`, so be permissive when accessLevel is undefined).
  const hasCreateEdit = Boolean(perms?.testPermission?.([8, 10, 11, 12, 13], ['Create-EditUsers']));
  const hasEditRole = Boolean(perms?.testPermission?.([8, 9, 10, 11, 12, 13], ['EditRole']));
  const hasDeleteRole = Boolean(perms?.testPermission?.([8, 9, 10, 11, 12, 13], ['DeleteRole']));

  let levelAllowed = false;
  if (typeof accessLevel === 'number') {
    levelAllowed =
      accessLevel === 8 ||
      (accessLevel === 13 && (perm_level ?? 999) <= 12) ||
      (accessLevel === 12 && (perm_level ?? 999) <= 10) ||
      (accessLevel === 10 && (perm_level ?? 999) <= 10);
  } else {
    // No explicit access level supplied in `perms` — tests expect permissive
    // behavior based solely on `testPermission`.
    levelAllowed = true;
  }

  let hierarchyAllowed: boolean;
  if (typeof accessLevel === 'number') {
    hierarchyAllowed = !sharedHierarchyOrHigher || accessLevel === 13 || accessLevel === 12;
  } else {
    // No explicit access level: treat as a normal user. If the target user
    // shares a higher/equal hierarchy, deny editing/deleting.
    hierarchyAllowed = !sharedHierarchyOrHigher;
  }

  const canEllipsis =
    currentUsername !== email && levelAllowed && hierarchyAllowed && hasCreateEdit;

  return {
    showViewOnly: !canEllipsis,
    canEdit: canEllipsis && (hasEditRole || hasCreateEdit),
    canDelete: canEllipsis && (hasDeleteRole || hasCreateEdit),
  };
}

/**
 * Backwards-compatible helpers used by older tests and callers.
 */
export function shouldShowViewOnly(
  perm_level_or_user: number | { perm_level?: number; sharedHierarchyOrHigher?: boolean } | UserRow,
  sharedHierarchyOrHigher?: boolean,
  perms?: any,
  currentUsername?: string,
): boolean {
  if (typeof perm_level_or_user === 'object' && 'email' in (perm_level_or_user as any)) {
    const user = perm_level_or_user as UserRow;
    return getUserActionFlags(user, perms, currentUsername ?? '__not_current__').showViewOnly;
  }
  const perm_level =
    typeof perm_level_or_user === 'number' ? perm_level_or_user : perm_level_or_user?.perm_level;
  const user = { email: undefined, perm_level, sharedHierarchyOrHigher } as unknown as UserRow;
  return getUserActionFlags(user, perms, currentUsername ?? '__not_current__').showViewOnly;
}

export function canShowEllipsisMenu(
  emailOrUser: string | UserRow,
  perm_level?: number,
  sharedHierarchyOrHigher?: boolean,
  perms?: any,
  currentUsername?: string,
): boolean {
  const user: UserRow =
    typeof emailOrUser === 'string'
      ? ({ email: emailOrUser, perm_level, sharedHierarchyOrHigher } as unknown as UserRow)
      : emailOrUser;
  return !getUserActionFlags(user, perms, currentUsername ?? '__not_current__').showViewOnly;
}

/**
 * filterUsersBySearchTerm
 * Returns the subset of `rows` matching `searchTerm` across name, role_name, email, and create_by_email.
 */
export function filterUsersBySearchTerm(rows: UserRow[] = [], searchTerm?: string | null): UserRow[] {
  if (!searchTerm) return rows;
  const cleaned = String(searchTerm || '').toLowerCase().replace(/\s/g, '');
  return (rows || []).filter((r) => {
    const name = String(r.name || '').toLowerCase().replace(/\s/g, '');
    const roles = String(r.role_name || '').toLowerCase().replace(/\s/g, '');
    const email = String(r.email || '').toLowerCase().replace(/\s/g, '');
    const created = String(r.create_by_email || '').toLowerCase().replace(/\s/g, '');
    return (
      name.includes(cleaned) || roles.includes(cleaned) || email.includes(cleaned) || created.includes(cleaned)
    );
  });
}

/**
 * normalizeRowIds
 * Ensures each row has a stable `id` property based on `rowKey` or fallback fields.
 */
export function normalizeRowIds(rows: UserRow[] = [], rowKey: string = 'userId'): UserRow[] {
  return (rows || []).map((r) => ({ ...r, id: r.id ?? r[rowKey] ?? r.userId }));
}
